
import { offlineDb, CachedImage } from '@/db/offlineDb';
import { networkService } from '@/services/network.service';
import OfflineLogger from '@/utils/offlineLogger';


class EnhancedImageCacheService {
  private cache = new Map<string, string>();
  /** Mappe di richieste in corso per evitare fetch duplicati */
  private inflight = new Map<string, Promise<string>>();
  private queue: Array<() => void> = [];
  private controllers = new Map<string, AbortController>();
  private activeFetches = 0;
  private readonly MAX_CONCURRENT = 4;
  private readonly CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 giorni

  private processQueue() {
    if (this.activeFetches >= this.MAX_CONCURRENT) return;
    const next = this.queue.shift();
    if (next) {
      this.activeFetches++;
      next();
    }
  }

  private enqueueFetch(productId: string, task: () => Promise<string>): Promise<string> {
    if (this.inflight.has(productId)) {
      return this.inflight.get(productId)!;
    }

    const promise = new Promise<string>((resolve, reject) => {
      const execute = () => {
        task()
          .then(resolve, reject)
          .finally(() => {
            if (this.activeFetches > 0) {
              this.activeFetches--;
            }
            this.inflight.delete(productId);
            this.processQueue();
          });
      };

      if (this.activeFetches < this.MAX_CONCURRENT) {
        this.activeFetches++;
        execute();
      } else {
        this.queue.push(execute);
      }
    });

    this.inflight.set(productId, promise);
    return promise;
  }

  async getImageUrl(productId: string, imageUrl: string): Promise<string | null> {
    if (!imageUrl) return null;

    // Se abbiamo già l'URL in memoria, restituiscilo
    if (this.cache.has(productId)) {
      return this.cache.get(productId)!;
    }

    try {
      // Se siamo online, prova a scaricare e cachare l'immagine
      if (networkService.isOnline()) {
        return await this.fetchAndCacheImage(productId, imageUrl);
      } else {
        // Se siamo offline, cerca nell'IndexedDB
        return await this.getImageFromCache(productId);
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        OfflineLogger.warn(
          'EnhancedImageCacheService.getImageUrl',
          'Image fetch aborted'
        );
      } else {
        OfflineLogger.error('EnhancedImageCacheService.getImageUrl', error);
      }
      return '/placeholder.svg'; // Fallback
    }
  }

  private async downloadAndCacheImage(productId: string, imageUrl: string): Promise<string> {
    const controller = new AbortController();
    this.controllers.set(productId, controller);
    try {
      // Prima controlla se l'immagine è già in cache e valida
      const cachedImage = await this.getImageFromCache(productId);
      if (cachedImage) {
        this.cache.set(productId, cachedImage);
        return cachedImage;
      }

      // Scarica l'immagine
      const response = await fetch(imageUrl, { signal: controller.signal });
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      // Salva in cache
      await this.saveImageToCache(productId, imageUrl, blob);
      this.cache.set(productId, objectUrl);

      //OfflineLogger.info('EnhancedImageCacheService', `Image cached for product ${productId}`);
      return objectUrl;
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        OfflineLogger.warn(
          'EnhancedImageCacheService.downloadAndCacheImage',
          'Image fetch aborted',
          error
        );
      } else {
        OfflineLogger.error(
          'EnhancedImageCacheService.downloadAndCacheImage',
          error
        );
      }
      throw error;
    } finally {
      this.controllers.delete(productId);
    }
  }

  private fetchAndCacheImage(productId: string, imageUrl: string): Promise<string> {
    return this.enqueueFetch(productId, () => this.downloadAndCacheImage(productId, imageUrl));
  }

  private async getImageFromCache(productId: string): Promise<string | null> {
    try {
      // Prima controlla la memoria
      if (this.cache.has(productId)) {
        return this.cache.get(productId)!;
      }

      // Cerca nell'IndexedDB
      const cachedImages = await offlineDb.imageCache.get(productId);
      
      if (!cachedImages) {
        return null;
      }

      // Controlla se non è scaduta
      if (Date.now() > cachedImages.expires_at) {
        await offlineDb.imageCache.delete(productId);
        return null;
      }

      // Crea object URL dal blob salvato
      const objectUrl = URL.createObjectURL(cachedImages.blob);
      this.cache.set(productId, objectUrl);
      
      return objectUrl;

    } catch (error) {
      OfflineLogger.error('EnhancedImageCacheService.getImageFromCache', error);
      return null;
    }
  }

  /** Verifica se un'immagine è già presente in cache senza scaricarla. */
  async isCached(productId: string): Promise<boolean> {
    const cached = await offlineDb.imageCache.get(productId);
    return !!cached && Date.now() <= cached.expires_at;
  }

  private async saveImageToCache(productId: string, url: string, blob: Blob): Promise<void> {
    try {
      const now = Date.now();
      const cacheEntry: CachedImage = {
        id: productId,
        url,
        blob,
        cached_at: now,
        expires_at: now + this.CACHE_DURATION
      };

      // Salva in IndexedDB
      await offlineDb.imageCache.put(cacheEntry);
      //OfflineLogger.info('EnhancedImageCacheService', `Image cached for ${productId}`);
      
    } catch (error) {
      OfflineLogger.error('EnhancedImageCacheService.saveImageToCache', error);
    }
  }

  cancelAll(): Promise<unknown>[] {
    const inflightPromises = Array.from(this.inflight.values()).map(p =>
      p.catch(error => {
        if ((error as any)?.name !== 'AbortError') {
          throw error;
        }
      })
    );

    this.controllers.forEach(controller => {
      try {
        controller.abort();
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          OfflineLogger.error('EnhancedImageCacheService.cancelAll', error);
        }
      }
    });

    this.controllers.clear();
    this.inflight.clear();
    this.queue = [];
    this.activeFetches = 0;

    return inflightPromises;
  }

  async clearCache(): Promise<void> {
    // Cancella eventuali download in corso
    const inflightPromises = this.cancelAll();
    await Promise.allSettled(inflightPromises);
    // Pulisci URLs in memoria
    this.cache.forEach(url => URL.revokeObjectURL(url));
    this.cache.clear();
  }

  /** Rimuove un'immagine specifica dalla cache. */
  async invalidateImage(productId: string): Promise<void> {
    this.cache.delete(productId);
    await offlineDb.imageCache.delete(productId);
  }

  /**
   * Scarica e memorizza in cache un elenco di immagini prodotto.
   * Utilizzato durante la sincronizzazione iniziale per rendere
   * disponibili le immagini anche in assenza di connessione.
   */
  async preloadImages(
    productPhotos: Array<{ id: string; photo?: string }>,
    onProgress?: (downloaded: number, total: number) => void
  ): Promise<void> {
    if (!networkService.isOnline()) {
      OfflineLogger.info(
        'EnhancedImageCacheService',
        'Skipping image preload - offline mode'
      );
      return;
    }

    const validPhotos = productPhotos.filter(p => p.id && p.photo);
    const toDownload: Array<{ id: string; photo: string }> = [];

    for (const item of validPhotos) {
      const cached = await this.isCached(item.id);
      if (!cached) {
        toDownload.push({ id: item.id, photo: item.photo! });
      }
    }

    const total = toDownload.length;
    onProgress?.(0, total);

    let downloaded = 0;
    try {
      for (const img of toDownload) {
        await this.fetchAndCacheImage(img.id, img.photo);
        downloaded++;
        onProgress?.(downloaded, total);
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        OfflineLogger.info(
          'EnhancedImageCacheService',
          'Image preload aborted'
        );
        return;
      }
      throw error;
    }

    OfflineLogger.info(
      'EnhancedImageCacheService',
      `Preloaded ${toDownload.length} images`
    );
  }
}

export const enhancedImageCacheService = new EnhancedImageCacheService();
